import React from "react";

import MuiTable from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";

import TableHead from "./TableHead";
import TableSkeleton from "./TableSkeleton";
import ActionMenu from "components/ActionMenu";
import { MenuItem } from "components/ActionMenu/types";

import { ReactComponent as ActionsIcon } from "assets/images/actionMenu/actions-menu-icon.svg";

import { DataTableColumn } from "../types";

import { styles } from "./styles";

interface TableProps<T extends { id: string }> {
  columns: DataTableColumn<T>[];
  rows: T[];
  actions?: MenuItem<T>[];
  skeletonRowCount?: number;
  loading?: boolean;
}

const Table = <T extends { id: string }>(props: TableProps<T>) => {
  const {
    columns,
    rows,
    actions = [],
    loading = false,
    skeletonRowCount,
  } = props;

  const hasActions = actions.length > 0;

  if (loading) {
    return (
      <TableSkeleton<T>
        columns={columns}
        rowCount={skeletonRowCount}
        withActions={hasActions}
      />
    );
  }

  const renderCellContent = (column: DataTableColumn<T>, row: T) => {
    if (column.renderCell) {
      return column.renderCell(row);
    }

    const { field, valueFormatter, color, secure, maxLines = 1 } = column;

    const raw = row[field];
    const display = valueFormatter ? valueFormatter(raw) : String(raw ?? "");

    return (
      <Typography
        variant="body2"
        color={color}
        noWrap={maxLines === 1}
        sx={maxLines > 1 ? styles.lineClamp(maxLines) : undefined}
        data-posthog-mask={secure ? "true" : undefined}
      >
        {display}
      </Typography>
    );
  };

  const renderCell = (row: T, column: DataTableColumn<T>) => {
    const { field, width, minWidth, align } = column;

    return (
      <TableCell
        key={field.toString()}
        sx={{ width, maxWidth: 0, minWidth }}
        align={align}
      >
        {renderCellContent(column, row)}
      </TableCell>
    );
  };

  return (
    <TableContainer sx={{ mt: -1 }}>
      <MuiTable>
        <TableHead<T> columns={columns} withActions={hasActions} />
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {columns.map((col) => renderCell(row, col))}
              {hasActions && (
                <TableCell
                  key={`${row.id}-actions`}
                  sx={{ width: "5%" }}
                  align="right"
                >
                  <ActionMenu
                    mainIcon={<ActionsIcon />}
                    menuId={`action-menu-${row.id}`}
                    actions={actions}
                    row={row}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
